import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../react-auth0-spa';
import InputField from '../../components/ui/InputField';
import SelectField from '../../components/ui/SelectField';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import CustomButton from '../../components/CustomButton';
import { paths } from '../../routes';
import { COUNTRIES, getCountryLabel } from '../onboarding/countries';
import CancelAccountSection from './CancelAccountSection';
import ReactivateAccountSection from './ReactivateAccountSection';
import InvoiceIssuerSection from './InvoiceIssuerSection';
import InvoiceCircleSection from '../../components/invoice/InvoiceCircleSection';
import InvoiceDispatchEmailSection from '../../components/invoice/InvoiceDispatchEmailSection';
import InvoiceBundleExtrasSection from '../../components/invoice/InvoiceBundleExtrasSection';
import AuditExportSection from '../../components/invoice/AuditExportSection';
import { AccountLegalTexts } from './LegalTextsSection';
import { AccountPrivacyOfficer } from './PrivacyOfficerSection';
import SeoSection from './SeoSection';
import PaymentProviderSection from './PaymentProviderSection';
import UseCasesSection from './UseCasesSection';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import UsersSection from './UsersSection';
import SenderAddressSection from './SenderAddressSection';
import SegmentedTabs from '../../components/SegmentedTabs';
import { cn } from '../../utils/utils';
import {
    useUpdateAnredeMutation,
    useUpdateBankDetailsMutation,
    useUpdateBrandingMutation,
    useUpdateContactMutation,
    useUpdateLegalNoticeMutation,
    useUpdateOrganizationMutation,
    useUpdateProfileMutation,
} from '../../api/account/accountApiSlice';
import { tenantSelector } from '../../selector/authenticationSelector';
import { isSelfServiceTenant } from '../../common/tenantStore';
import { useViewedTenantAccount } from '../../hooks/useViewedTenantAccount';
import {
    enqueueErrorSnackbar,
    enqueueSuccessSnackbar,
} from '../../action/snackbarCommands';
import { isValidBIC, isValidIBAN } from '../../utils/bankValidation';
import {
    AccountUser,
    TenantAnrede,
    TenantOrganizationType,
} from '../../api/account/accountApiTypes';
import { useOrganizationTypeOptions } from '../../common/organizationTypes';
import { applyTenantAccent, hexToTriplet } from '../../common/tenantAccentColor';
import { errorMessageFrom } from '../../utils/errorMessage';

type InitialSnapshot = {
    personal: { firstName: string, lastName: string, phone: string };
    org: {
        organizationName: string;
        country: string;
        organizationType: TenantOrganizationType;
        isKleinunternehmer: boolean;
    };
    bank: {
        accountHolder: string;
        iban: string;
        bic: string;
        bankName: string;
    };
    contact: { contactEmail: string, contactPhone: string, website: string };
    legal: {
        street: string;
        postalCode: string;
        city: string;
        managingDirector: string;
        hrb: string;
        hrbCourt: string;
        supervisoryBoardChair: string;
        vatId: string;
        taxNumber: string;
    };
};

// Branding colors are stored backend-side as a CSS-ready RGB triplet string
// (e.g. "0, 84, 117") so the end-user app can drop them straight into a CSS
// variable. The native <input type="color"> only speaks "#rrggbb", so we
// convert at the UI boundary: triplet → hex to seed the picker, hex → sent
// verbatim on save (the backend's normalizeBrandingColor turns hex back into a
// triplet). DEFAULT_PRIMARY_HEX is a neutral fallback when no color is stored.
const DEFAULT_PRIMARY_HEX = '#000000';

const clampByte = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

// "0, 84, 117" → "#005475". Returns the neutral default for anything that
// isn't a valid three-number triplet.
const tripletToHex = (triplet: string | undefined | null): string => {
    if (!triplet) return DEFAULT_PRIMARY_HEX;
    const match = triplet.trim().match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/);
    if (!match) return DEFAULT_PRIMARY_HEX;
    const [r, g, b] = [match[1], match[2], match[3]].map((v) => clampByte(Number(v)));
    const hex = [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('');
    return `#${hex}`;
};

// Branding image rules mirror the backend's isAllowedBrandingImage.
const MAX_BRANDING_FILE_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_LOGO_MIME = new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
]);
const ALLOWED_FAVICON_MIME = new Set([
    'image/png',
    'image/x-icon',
    'image/vnd.microsoft.icon',
]);

type AccountTab = 'organisation' | 'payments' | 'usecases' | 'communication';

/** Which tab holds each deep-link anchor. Every `id` on a section below has an
 *  entry here — a section reachable by `/account#…` (from the dashboard setup
 *  checklist) is unreachable unless its tab gets selected first. */
const TAB_BY_ANCHOR: Record<string, AccountTab> = {
    'bank-details': 'payments',
    // Linked from the Services area when a choice there needs a Stripe
    // connection this tenant does not have.
    'payment-provider': 'payments',
    // Linked from Services when "invoiced separately" needs an issuer.
    'invoice-issuer': 'payments',
    'use-cases': 'usecases',
    contact: 'communication',
    'customer-emails': 'communication',
    'legal-notice': 'organisation',
    // Sits beside the legal notice on the organisation tab. Nothing links to
    // `/account#legal-texts` today, so this entry is what keeps the docstring
    // above true rather than a fix for a broken link — the section carries an
    // `id`, and an anchor whose tab is never selected scrolls to nothing.
    'legal-texts': 'organisation',
    // Branding joined contact and customer e-mails on 2026-09-16: all three
    // are how this tenant appears to its own customers. The tab it used to
    // have was a signpost to one section — the shape ADR-0005's 2026-09-12
    // amendment warned about when it relabelled "Experience" to "Branding".
    branding: 'communication',
    // NOTE: no `booking-page` entry. That section left Account for Services
    // on 2026-09-12 (ADR-0005 amendment); the dashboard checklist links to
    // `/rental-products#booking-page` and ServicesPage maps it to its
    // usage-flow tab. The entry here outlived the section it pointed at.
};

interface TaxIdFieldsProps {
    vatId: string;
    onVatIdChange: (value: string) => void;
    taxNumber: string;
    onTaxNumberChange: (value: string) => void;
    invoiceIssuer: string | null | undefined;
}

/**
 * USt-IdNr. and Steuernummer (EN 16931 BT-31 / BT-32). A tenant kolula
 * invoices for (ADR-0012 Mode A) cannot issue without one of the two —
 * issuance refuses `seller_incomplete` — so for them neither calls itself
 * optional and a hint says one is needed.
 */
const TaxIdFields = ({ vatId, onVatIdChange, taxNumber, onTaxNumberChange, invoiceIssuer }: Readonly<TaxIdFieldsProps>) => {
    const { t } = useTranslation();
    const invoicing = invoiceIssuer === 'kolula';
    return (
        <div>
            <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                <InputField
                    label={t(invoicing ? 'account.vat_id_label' : 'account.vat_id')}
                    value={vatId}
                    onChange={onVatIdChange}
                    placeholder={'DE123456789'}
                />
                <InputField
                    label={t(invoicing ? 'account.tax_number_label' : 'account.tax_number')}
                    value={taxNumber}
                    onChange={onTaxNumberChange}
                    placeholder={'12/345/67890'}
                />
            </div>
            {invoicing && (
                <p className={'mt-1.5 text-xs text-foreground/60'}>{t('account.tax_id_invoicing_hint')}</p>
            )}
        </div>
    );
};

/** A blank optional field is sent as absent, not as an empty string. */
const optionalText = (value: string): string | undefined => value.trim() || undefined;

type AccountUnit = 'personal' | 'org' | 'bank' | 'contact' | 'legal' | 'branding';

/** The touched-state names of each unit's fields, cleared on "Abbrechen". */
const UNIT_FIELDS: Record<AccountUnit, string[]> = {
    personal: ['firstName', 'lastName'],
    org: ['organizationName'],
    bank: ['accountHolder', 'iban', 'bic', 'bankName'],
    contact: ['contactEmail', 'contactPhone'],
    legal: ['street', 'postalCode', 'city', 'managingDirector'],
    branding: [],
};

type Setter<T> = (value: T) => void;
interface UnitFormSetters {
    setFirstName: Setter<string>; setLastName: Setter<string>; setPhone: Setter<string>;
    setOrganizationName: Setter<string>; setCountry: Setter<string>;
    setOrganizationType: Setter<TenantOrganizationType>; setIsKleinunternehmer: Setter<boolean>;
    setAccountHolder: Setter<string>; setIban: Setter<string>; setBic: Setter<string>; setBankName: Setter<string>;
    setContactEmail: Setter<string>; setContactPhone: Setter<string>; setWebsite: Setter<string>;
    setStreet: Setter<string>; setPostalCode: Setter<string>; setCity: Setter<string>;
    setManagingDirector: Setter<string>; setHrb: Setter<string>; setHrbCourt: Setter<string>;
    setSupervisoryBoardChair: Setter<string>;
    setVatId: Setter<string>; setTaxNumber: Setter<string>;
}

/** "Abbrechen" on one unit: its fields back to the saved snapshot. */
const restoreUnitForm = (unit: AccountUnit, saved: InitialSnapshot, set: UnitFormSetters): void => {
    switch (unit) {
        case 'personal':
            set.setFirstName(saved.personal.firstName);
            set.setLastName(saved.personal.lastName);
            set.setPhone(saved.personal.phone);
            break;
        case 'org':
            set.setOrganizationName(saved.org.organizationName);
            set.setCountry(saved.org.country);
            set.setOrganizationType(saved.org.organizationType);
            set.setIsKleinunternehmer(saved.org.isKleinunternehmer);
            break;
        case 'bank':
            set.setAccountHolder(saved.bank.accountHolder);
            set.setIban(saved.bank.iban);
            set.setBic(saved.bank.bic);
            set.setBankName(saved.bank.bankName);
            break;
        case 'contact':
            set.setContactEmail(saved.contact.contactEmail);
            set.setContactPhone(saved.contact.contactPhone);
            set.setWebsite(saved.contact.website);
            break;
        case 'legal':
            set.setStreet(saved.legal.street);
            set.setPostalCode(saved.legal.postalCode);
            set.setCity(saved.legal.city);
            set.setManagingDirector(saved.legal.managingDirector);
            set.setHrb(saved.legal.hrb);
            set.setHrbCourt(saved.legal.hrbCourt);
            set.setSupervisoryBoardChair(saved.legal.supervisoryBoardChair);
            set.setVatId(saved.legal.vatId);
            set.setTaxNumber(saved.legal.taxNumber);
            break;
        default:
            break;
    }
};

const AccountPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useAuth0();

    // Which tenant is on screen, may the caller edit it, and what does it have
    // deposited. One definition, shared with the Services area — see
    // `useViewedTenantAccount`, which carries the reasoning for each case.
    const {
        isMaster,
        isSelfService,
        ownsViewedTenant,
        accountTenant,
        accountUser,
        readOnly,
        paymentIntegration,
    } = useViewedTenantAccount();
    const selectedTenant = useSelector(tenantSelector);

    const [updateProfile, { isLoading: personalSaving }] = useUpdateProfileMutation();
    const [updateOrganization, { isLoading: orgSaving }] = useUpdateOrganizationMutation();
    const [updateBankDetails, { isLoading: bankSaving }] = useUpdateBankDetailsMutation();
    const [updateContact, { isLoading: contactSaving }] = useUpdateContactMutation();
    const [updateLegalNotice, { isLoading: legalSaving }] = useUpdateLegalNoticeMutation();
    const [updateAnrede, { isLoading: anredeSaving }] = useUpdateAnredeMutation();
    const [updateBranding, { isLoading: brandingSaving }] = useUpdateBrandingMutation();

    // Branding (primary color, logo, favicon) is tenant-editable via the
    // Branding section below (PATCH /account/branding); the end-user app falls
    // back to the kolula defaults for any branding field a tenant hasn't set.
    //
    // Switching the end-user app ON is no longer here: it moved to
    // Services → Nutzungsablauf, next to the check-in/check-out flow it belongs
    // with (PO, 2026-09-12; ADR-0005 amendment).

    // Email salutation as stored; edited as a draft behind "Bearbeiten" and
    // saved explicitly like every other unit (PO, 2026-10-12).
    // Absent ⇒ the formal `sie` default (#1642).
    const anrede: TenantAnrede = accountTenant?.anrede ?? 'sie';
    // Edited as a draft of the stored value, like the sub-sections.
    const anredeEdit = useEditableDraft<TenantAnrede>(anrede);
    const { cancel: cancelAnrede } = anredeEdit;

    /**
     * Account settings are grouped into tabs (CEO, 2026-08-13): the page had
     * eleven stacked sections and was getting hard to scan. Grouping follows
     * what a setting belongs to — the legal block is the company's own address,
     * so it sits with the organisation rather than with what customers receive.
     *
     * The tabs stay as they are (PO, 2026-08-18). The Payments tab in
     * particular is not dissolved now that the per-use-case configuration has
     * moved to Services: it keeps the bank details AND gains the payment
     * provider, because both are credentials to deposit, and keeping them
     * together makes access to them ONE thing to permission (ADR-0010).
     */
    const [activeTab, setActiveTab] = useState<AccountTab>('organisation');
    // One resolution of "may the caller edit sections that read/write their
    // OWN account" — a master editing a foreign tenant gets the by-slug
    // variants outside the fieldset instead.
    const ownAccountSections = !isMaster || ownsViewedTenant;
    // Reported up by UseCasesSection so its unsaved edits reach the same
    // beforeunload guard as every other section's.
    const [useCasesDirty, setUseCasesDirty] = useState(false);

    /**
     * One editable unit per section (design-decisions › Bearbeiten und
     * Speichern): the form shows the saved values locked until "Bearbeiten",
     * "Abbrechen" restores them, a successful save closes the unit.
     */
    const [editing, setEditing] = useState<Partial<Record<AccountUnit, boolean>>>({});
    const isEditing = (unit: AccountUnit) => editing[unit] === true;
    const startEdit = (unit: AccountUnit) => {
        // No holder stored yet: propose the organization's name — for most
        // tenants the legal entity is the account holder. Only on Bearbeiten,
        // so the view never shows a proposal as if it were saved.
        if (unit === 'bank' && !accountHolder.trim()) {
            setAccountHolder(initialRef.current?.org.organizationName ?? '');
        }
        setEditing((prev) => ({ ...prev, [unit]: true }));
    };
    const closeUnit = (unit: AccountUnit) => setEditing((prev) => ({ ...prev, [unit]: false }));

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [country, setCountry] = useState('');

    // Bank details & contact
    const [accountHolder, setAccountHolder] = useState('');
    const [iban, setIban] = useState('');
    const [bic, setBic] = useState('');
    const [bankName, setBankName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [website, setWebsite] = useState('');

    // Legal notice / Impressum
    const [organizationType, setOrganizationType] = useState<TenantOrganizationType>('company');
    // #1353 §19 UStG. Lives in Account rather than Configuration because it is
    // a legal fact about the tenant, not a product behaviour (ADR-0005).
    const [isKleinunternehmer, setIsKleinunternehmer] = useState(false);
    const [street, setStreet] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [city, setCity] = useState('');
    const [managingDirector, setManagingDirector] = useState('');
    const [hrb, setHrb] = useState('');
    const [hrbCourt, setHrbCourt] = useState('');
    const [supervisoryBoardChair, setSupervisoryBoardChair] = useState('');
    const [vatId, setVatId] = useState('');
    const [taxNumber, setTaxNumber] = useState('');

    // Reserved slug (still surfaced in the disabled "Coming soon" web-app
    // section; activation flow lands in a separate PR).
    const [slug, setSlug] = useState('');

    // Branding section — kept independent of the snapshot/dirty machinery the
    // text sections share, because its "dirty" state is a color diff + selected
    // files, not plain string comparisons. `primaryColorHex` seeds from the
    // stored triplet on init; the file inputs start empty and fall back to the
    // currently-stored asset URLs for their previews.
    const [primaryColorHex, setPrimaryColorHex] = useState(DEFAULT_PRIMARY_HEX);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [faviconFile, setFaviconFile] = useState<File | null>(null);
    const [brandingError, setBrandingError] = useState<string | null>(null);

    // Per-section error messages (stay independent of the form state).
    const [personalError, setPersonalError] = useState<string | null>(null);
    const [orgError, setOrgError] = useState<string | null>(null);
    const [bankError, setBankError] = useState<string | null>(null);
    const [contactError, setContactError] = useState<string | null>(null);
    const [legalError, setLegalError] = useState<string | null>(null);
    const ERROR_SETTERS: Partial<Record<AccountUnit, (message: string | null) => void>> = {
        personal: setPersonalError,
        org: setOrgError,
        bank: setBankError,
        contact: setContactError,
        legal: setLegalError,
        branding: setBrandingError,
    };

    // Per-field touched state — only show inline errors (required-empty,
    // format-invalid) after the user has interacted with and left the
    // field, not while typing.
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const markTouched = (name: string) =>
        setTouched((prev) => prev[name] ? prev : { ...prev, [name]: true });

    // Initial-values snapshot per section, populated alongside form state
    // in the init effect below. Used to derive `*Dirty` flags so each Save
    // button greys out when nothing has changed since the last save.
    const initialRef = useRef<InitialSnapshot | null>(null);

    // Init guard — pre-fill form state from the API exactly once. CLAUDE.md
    // ("useEffect: Separate Init and Reaction"): keep initialisation in its
    // own effect with a ref-based guard so refetches after a save don't
    // overwrite anything the user is currently typing.
    /**
     * The tenant this form was seeded from — not a boolean.
     *
     * A one-shot boolean guard latched on the FIRST payload to arrive, and two
     * account queries race here: for a master who owns the tenant on screen,
     * the by-slug read returns `{ tenant }` with **no `user`**
     * (`accountController` serializes only the tenant there). Seeding from it
     * wrote `''` into Vorname/Nachname/Telefon and latched, so the real account
     * arriving a moment later could no longer fill them — the fields stayed
     * empty, and Save stayed grey because `personalDirty` was false.
     *
     * Keying on the tenant instead also re-seeds on a tenant switch, which the
     * boolean never did (CLAUDE.md, "Reset State on Tenant / Context Switch").
     */
    const seededForTenantRef = useRef<string | null>(null);
    useEffect(() => {
        if (!accountTenant) return;
        const seedKey = accountTenant.slug ?? '';
        if (seededForTenantRef.current === seedKey) return;
        // Wait for the payload that actually carries the user. In a read-only
        // master view there will never be one and the personal section is not
        // rendered, so seeding without it is correct there.
        if (!readOnly && !accountUser) return;

        const tenant = accountTenant;
        const u: AccountUser | undefined = accountUser;
        const personal = {
            firstName: u?.firstName ?? '',
            lastName: u?.lastName ?? '',
            phone: u?.phone ?? '',
        };
        const org = {
            organizationName: tenant.organizationName ?? '',
            country: tenant.country ?? '',
            organizationType: tenant.organizationType ?? 'company',
            isKleinunternehmer: tenant.isKleinunternehmer === true,
        };
        const bank = {
            accountHolder: tenant.bank?.accountHolder ?? '',
            iban: tenant.bank?.iban ?? '',
            bic: tenant.bank?.bic ?? '',
            bankName: tenant.bank?.bankName ?? '',
        };
        const contact = {
            contactEmail: tenant.contact?.email ?? '',
            contactPhone: tenant.contact?.phone ?? '',
            website: tenant.contact?.website ?? '',
        };
        const legal = {
            street: tenant.legalNotice?.street ?? '',
            postalCode: tenant.legalNotice?.postalCode ?? '',
            city: tenant.legalNotice?.city ?? '',
            managingDirector: tenant.legalNotice?.managingDirector ?? '',
            hrb: tenant.legalNotice?.hrb ?? '',
            hrbCourt: tenant.legalNotice?.hrbCourt ?? '',
            supervisoryBoardChair: tenant.legalNotice?.supervisoryBoardChair ?? '',
            vatId: tenant.legalNotice?.vatId ?? '',
            taxNumber: tenant.legalNotice?.taxNumber ?? '',
        };

        setFirstName(personal.firstName);
        setLastName(personal.lastName);
        setPhone(personal.phone);
        setOrganizationName(org.organizationName);
        setCountry(org.country);
        // As stored; the organization-name proposal is made on "Bearbeiten".
        setAccountHolder(bank.accountHolder);
        setIban(bank.iban);
        setBic(bank.bic);
        setBankName(bank.bankName);
        setContactEmail(contact.contactEmail);
        setContactPhone(contact.contactPhone);
        setWebsite(contact.website);
        setPrimaryColorHex(tripletToHex(tenant.branding?.primaryColor));
        setOrganizationType(org.organizationType);
        setIsKleinunternehmer(org.isKleinunternehmer);
        setStreet(legal.street);
        setPostalCode(legal.postalCode);
        setCity(legal.city);
        setManagingDirector(legal.managingDirector);
        setHrb(legal.hrb);
        setHrbCourt(legal.hrbCourt);
        setSupervisoryBoardChair(legal.supervisoryBoardChair);
        setVatId(legal.vatId);
        setTaxNumber(legal.taxNumber);

        if (tenant.slug) {
            setSlug(tenant.slug);
        }

        initialRef.current = {
            personal,
            org,
            bank,
            contact,
            legal,
        };
        seededForTenantRef.current = seedKey;
        // A switch to another tenant closes every open unit (#909/#929).
        setEditing({});
        cancelAnrede();
    }, [accountTenant, accountUser, readOnly, cancelAnrede]);

    // Scroll to hash anchor when navigating from setup checklist (e.g. /account#bank-details).
    // Since the sections were grouped into tabs, the anchor's section is not in
    // the DOM unless its tab is active — so select the owning tab first, then
    // scroll on the next frame. Without this the setup-checklist links resolve
    // to no element and silently do nothing.
    const location = useLocation();
    useEffect(() => {
        if (!location.hash) return;
        const anchor = location.hash.slice(1);
        const tab = TAB_BY_ANCHOR[anchor];
        if (tab) setActiveTab(tab);
        // Small delay to let the newly selected tab render first
        const timer = setTimeout(() => {
            document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        return () => clearTimeout(timer);
    }, [location.hash]);

    const notifySuccess = () => {
        dispatch(enqueueSuccessSnackbar({ message: t('account.saved'), autoHideDuration: 3000 }));
    };
    const notifyError = (message: string) => {
        dispatch(enqueueErrorSnackbar({ message, autoHideDuration: 5000 }));
    };

    const handleSaveAnrede = async () => {
        try {
            await updateAnrede({ anrede: anredeEdit.value }).unwrap();
            notifySuccess();
            anredeEdit.finish();
        } catch (err) {
            notifyError(errorMessageFrom(err, t('account.error_generic'), t));
        }
    };

    /** Puts one unit's form back to its saved snapshot. */
    const restoreUnit = (unit: AccountUnit, saved: InitialSnapshot) => restoreUnitForm(unit, saved, {
        setFirstName, setLastName, setPhone,
        setOrganizationName, setCountry, setOrganizationType, setIsKleinunternehmer,
        setAccountHolder, setIban, setBic, setBankName,
        setContactEmail, setContactPhone, setWebsite,
        setStreet, setPostalCode, setCity, setManagingDirector, setHrb, setHrbCourt, setSupervisoryBoardChair,
        setVatId, setTaxNumber,
    });

    /** "Abbrechen": back to the saved values, no field marked as touched. */
    const cancelUnit = (unit: AccountUnit) => {
        const saved = initialRef.current;
        const fields = UNIT_FIELDS[unit];
        setTouched((prev) => Object.fromEntries(Object.entries(prev).filter(([name]) => !fields.includes(name))));
        if (saved) restoreUnit(unit, saved);
        if (unit === 'branding') {
            setPrimaryColorHex(currentPrimaryHex);
            setLogoFile(null);
            setFaviconFile(null);
        }
        ERROR_SETTERS[unit]?.(null);
        closeUnit(unit);
    };

    const handleSavePersonal = async () => {
        setPersonalError(null);
        try {
            await updateProfile({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phone: phone.trim() || undefined,
            }).unwrap();
            if (initialRef.current) {
                initialRef.current = {
                    ...initialRef.current,
                    personal: { firstName, lastName, phone },
                };
            }
            notifySuccess();
            closeUnit('personal');
        } catch (err) {
            const message = errorMessageFrom(err, t('account.error_generic'), t);
            setPersonalError(message);
            notifyError(message);
        }
    };

    const handleSaveOrganization = async () => {
        setOrgError(null);
        try {
            // Name, country and org type all belong to this section and are
            // written by a single PATCH /account/organization (#990) — no
            // Impressum required to change the type.
            await updateOrganization({
                organizationName: organizationName.trim(),
                country,
                organizationType,
                isKleinunternehmer,
            }).unwrap();
            // Municipalities and associations have no trade register, so the
            // backend drops a stored hrb/hrbCourt when the type moves away from
            // company (#1797). Mirror that here — the fields are hidden in that
            // state, so otherwise they'd silently hold a value the server no
            // longer has.
            const registerCleared = organizationType !== 'company';
            if (registerCleared) {
                setHrb('');
                setHrbCourt('');
                setSupervisoryBoardChair('');
            }
            if (initialRef.current) {
                initialRef.current = {
                    ...initialRef.current,
                    org: { organizationName, country, organizationType, isKleinunternehmer },
                    ...(registerCleared
                        ? { legal: { ...initialRef.current.legal, hrb: '', hrbCourt: '', supervisoryBoardChair: '' } }
                        : {}),
                };
            }
            notifySuccess();
            closeUnit('org');
        } catch (err) {
            const message = errorMessageFrom(err, t('account.error_generic'), t);
            setOrgError(message);
            notifyError(message);
        }
    };

    // Bank details and public contact are two independent sections, each with
    // its own endpoint (PATCH /account/bank-details and /account/contact) and
    // its own Save button — saving one never touches the other's fields or
    // loading state.
    const handleSaveBankDetails = async () => {
        setBankError(null);
        try {
            await updateBankDetails({
                accountHolder: accountHolder.trim(),
                iban: iban.trim().replace(/\s+/g, ''),
                bic: bic.trim(),
                bankName: bankName.trim(),
            }).unwrap();
            if (initialRef.current) {
                initialRef.current = {
                    ...initialRef.current,
                    bank: { accountHolder, iban, bic, bankName },
                };
            }
            notifySuccess();
            closeUnit('bank');
        } catch (err) {
            const message = errorMessageFrom(err, t('account.error_generic'), t);
            setBankError(message);
            notifyError(message);
        }
    };

    const handleSaveContact = async () => {
        setContactError(null);
        try {
            await updateContact({
                contactEmail: contactEmail.trim(),
                contactPhone: contactPhone.trim(),
                // Optional — omit (clear) when empty so we never store "".
                website: website.trim() || undefined,
            }).unwrap();
            if (initialRef.current) {
                initialRef.current = {
                    ...initialRef.current,
                    contact: { contactEmail, contactPhone, website },
                };
            }
            notifySuccess();
            closeUnit('contact');
        } catch (err) {
            const message = errorMessageFrom(err, t('account.error_generic'), t);
            setContactError(message);
            notifyError(message);
        }
    };

    const handleSaveLegalNotice = async () => {
        setLegalError(null);
        try {
            // Trade-register fields only apply to companies; municipalities
            // and associations have no HRB, so don't persist stale values.
            // organizationType is owned by the Organisation section and written
            // by PATCH /account/organization — never sent from here, so this
            // endpoint leaves the tenant column untouched.
            const isCompany = (initialRef.current?.org.organizationType ?? organizationType) === 'company';
            await updateLegalNotice({
                street: street.trim(),
                postalCode: postalCode.trim(),
                city: city.trim(),
                managingDirector: managingDirector.trim(),
                hrb: isCompany ? hrb.trim() || undefined : undefined,
                hrbCourt: isCompany ? hrbCourt.trim() || undefined : undefined,
                supervisoryBoardChair: isCompany ? optionalText(supervisoryBoardChair) : undefined,
                vatId: vatId.trim() || undefined,
                taxNumber: optionalText(taxNumber),
            }).unwrap();
            if (initialRef.current) {
                initialRef.current = {
                    ...initialRef.current,
                    legal: {
                        street, postalCode, city, managingDirector, hrb, hrbCourt, supervisoryBoardChair, vatId, taxNumber,
                    },
                };
            }
            notifySuccess();
            closeUnit('legal');
        } catch (err) {
            const message = errorMessageFrom(err, t('account.error_generic'), t);
            setLegalError(message);
            notifyError(message);
        }
    };

    // Branding "current" values come straight from the refreshed account (no
    // snapshot needed): the stored primary-color triplet as hex, plus the
    // currently-stored logo / favicon URLs used as preview fallbacks.
    const currentPrimaryHex = tripletToHex(accountTenant?.branding?.primaryColor);
    const currentLogoUrl = accountTenant?.branding?.logoUrl ?? null;
    const currentFaviconUrl = accountTenant?.branding?.faviconUrl ?? null;

    // Object-URL previews for newly-selected files; revoked on change/unmount.
    const logoPreview = useMemo(
        () => (logoFile ? URL.createObjectURL(logoFile) : null),
        [logoFile],
    );
    useEffect(() => () => {
        if (logoPreview) URL.revokeObjectURL(logoPreview);
    }, [logoPreview]);

    const faviconPreview = useMemo(
        () => (faviconFile ? URL.createObjectURL(faviconFile) : null),
        [faviconFile],
    );
    useEffect(() => () => {
        if (faviconPreview) URL.revokeObjectURL(faviconPreview);
    }, [faviconPreview]);

    // Validate a picked branding image against the backend rules (type + 2 MB).
    // Returns an i18n error key when invalid, or null when fine.
    const validateBrandingFile = (file: File, allowed: Set<string>): string | null => {
        if (!allowed.has(file.type.toLowerCase())) return t('account.branding_image_invalid_type');
        if (file.size > MAX_BRANDING_FILE_BYTES) return t('account.branding_image_too_large');
        return null;
    };

    const handleSelectLogo = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (!file) return;
        const err = validateBrandingFile(file, ALLOWED_LOGO_MIME);
        if (err) {
            setBrandingError(err);
            notifyError(err);
            return;
        }
        setBrandingError(null);
        setLogoFile(file);
    };

    const handleSelectFavicon = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (!file) return;
        const err = validateBrandingFile(file, ALLOWED_FAVICON_MIME);
        if (err) {
            setBrandingError(err);
            notifyError(err);
            return;
        }
        setBrandingError(null);
        setFaviconFile(file);
    };

    // Dirty when a file is staged or the picked color differs from the stored one.
    const brandingDirty =
        !!logoFile || !!faviconFile || primaryColorHex.toLowerCase() !== currentPrimaryHex.toLowerCase();

    const handleSaveBranding = async () => {
        setBrandingError(null);
        try {
            const colorChanged = primaryColorHex.toLowerCase() !== currentPrimaryHex.toLowerCase();
            await updateBranding({
                ...(colorChanged ? { primaryColor: primaryColorHex } : {}),
                ...(logoFile ? { logo: logoFile } : {}),
                ...(faviconFile ? { favicon: faviconFile } : {}),
            }).unwrap();
            // The admin UI wears the tenant's accent too; show a new one now,
            // not only after the next sign-in.
            if (colorChanged) applyTenantAccent(hexToTriplet(primaryColorHex));
            // Clear the staged files. The picked color already equals the value
            // the backend just stored (hex→triplet→hex round-trips losslessly),
            // so once the Account cache refetches, `currentPrimaryHex` matches
            // `primaryColorHex` and the section returns to "clean" on its own —
            // no extra setter needed (avoids a double-write on the color state).
            setLogoFile(null);
            setFaviconFile(null);
            notifySuccess();
            closeUnit('branding');
        } catch (err) {
            const message = errorMessageFrom(err, t('account.error_generic'), t);
            setBrandingError(message);
            notifyError(message);
        }
    };

    const lang = i18n.language;
    const countryOptions = COUNTRIES.map((c) => c.code);
    const countryLabels = Object.fromEntries(
        COUNTRIES.map((c) => [c.code, getCountryLabel(c.code, lang)]),
    );
    const orgTypeOptions = useOrganizationTypeOptions();
    // Trade-register fields (HRB/HRA + register court) only exist for
    // companies; municipalities and associations don't have them (#951).
    // The Impressum reads the organization's SAVED facts: an open, unsaved
    // edit of the organization must not change what the Impressum shows or sends.
    const savedOrg = initialRef.current?.org;
    const showRegisterFields = (savedOrg?.organizationType ?? organizationType) === 'company';
    const legalCompanyName = savedOrg?.organizationName ?? organizationName;
    const legalCountry = savedOrg?.country ?? country;
    const legalCountryLabel = legalCountry ? getCountryLabel(legalCountry, lang) : t('common.missing');

    const bankFieldsComplete = !!accountHolder.trim() && !!iban.trim() && !!bic.trim() && !!bankName.trim();
    // The contact section has its own endpoint (PATCH /account/contact), which
    // requires email + phone — so the section gates its Save on both. Website is
    // optional and not part of this gate.
    const contactComplete = !!contactEmail.trim() && !!contactPhone.trim();
    const legalNoticeComplete = !!street.trim() && !!postalCode.trim() && !!city.trim() && !!managingDirector.trim();

    // Format validation — IBAN and BIC. VAT ID is intentionally not validated
    // client-side: formats are country-specific and the backend stays
    // authoritative; UX surfaces backend rejection if invalid.
    const ibanInvalid = !!iban.trim() && !isValidIBAN(iban);
    const bicInvalid = !!bic.trim() && !isValidBIC(bic);
    const bankFormatValid = !ibanInvalid && !bicInvalid;

    // Required-empty inline errors — only after the field was touched, so
    // a fresh visit on an unfilled form doesn't show red borders everywhere.
    const requiredEmpty = (name: string, value: string) =>
        !!touched[name] && !value.trim();

    const firstNameRequired = requiredEmpty('firstName', firstName);
    const lastNameRequired = requiredEmpty('lastName', lastName);
    const organizationNameRequired = requiredEmpty('organizationName', organizationName);
    const accountHolderRequired = requiredEmpty('accountHolder', accountHolder);
    const ibanRequired = requiredEmpty('iban', iban);
    const bicRequired = requiredEmpty('bic', bic);
    const bankNameRequired = requiredEmpty('bankName', bankName);
    const contactEmailRequired = requiredEmpty('contactEmail', contactEmail);
    const contactPhoneRequired = requiredEmpty('contactPhone', contactPhone);
    const streetRequired = requiredEmpty('street', street);
    const postalCodeRequired = requiredEmpty('postalCode', postalCode);
    const cityRequired = requiredEmpty('city', city);
    const managingDirectorRequired = requiredEmpty('managingDirector', managingDirector);

    // Combined error state for fields with both required + format validation.
    const ibanFieldError = ibanRequired || (!!touched.iban && ibanInvalid);
    const ibanFieldErrorText = ibanRequired
        ? t('account.field_required')
        : t('account.iban_format_error');
    const bicFieldError = bicRequired || (!!touched.bic && bicInvalid);
    const bicFieldErrorText = bicRequired
        ? t('account.field_required')
        : t('account.bic_format_error');

    // Per-section dirty flags — `initialRef` is null until the API loads,
    // so default to "clean" (Save disabled) before init.
    const snapshot = initialRef.current;
    const personalDirty = snapshot
        ? firstName !== snapshot.personal.firstName ||
          lastName !== snapshot.personal.lastName ||
          phone !== snapshot.personal.phone
        : false;
    const orgDirty = snapshot
        ? organizationName !== snapshot.org.organizationName ||
          country !== snapshot.org.country ||
          organizationType !== snapshot.org.organizationType ||
          isKleinunternehmer !== snapshot.org.isKleinunternehmer
        : false;
    const bankDirty = snapshot
        ? accountHolder !== snapshot.bank.accountHolder ||
          iban !== snapshot.bank.iban ||
          bic !== snapshot.bank.bic ||
          bankName !== snapshot.bank.bankName
        : false;
    const contactDirty = snapshot
        ? contactEmail !== snapshot.contact.contactEmail ||
          contactPhone !== snapshot.contact.contactPhone ||
          website !== snapshot.contact.website
        : false;
    const legalDirty = snapshot
        ? street !== snapshot.legal.street ||
          postalCode !== snapshot.legal.postalCode ||
          city !== snapshot.legal.city ||
          managingDirector !== snapshot.legal.managingDirector ||
          hrb !== snapshot.legal.hrb ||
          hrbCourt !== snapshot.legal.hrbCourt ||
          supervisoryBoardChair !== snapshot.legal.supervisoryBoardChair ||
          vatId !== snapshot.legal.vatId ||
          taxNumber !== snapshot.legal.taxNumber
        : false;

    // Warn on tab close / refresh when any section has unsaved edits.
    // (In-app navigation isn't blocked — react-router-dom v6.3 doesn't
    // expose useBlocker; that's tracked separately as a follow-up.)
    const anyDirty = personalDirty || orgDirty || bankDirty || contactDirty || legalDirty || anredeEdit.isDirty
        || brandingDirty || useCasesDirty;
    useEffect(() => {
        if (!anyDirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [anyDirty]);


    // The form of each editable unit — rendered locked as the view and live
    // while editing (design-decisions › Bearbeiten und Speichern).
    const personalForm = (
        <>
            <div className={'flex flex-col gap-5'}>
                <div className={'text-sm text-foreground/60'}>
                    {t('account.email_label')}{' '}
                    <span className={'font-medium text-foreground'}>{user?.email}</span>
                </div>

                <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                    <InputField
                        label={t('account.first_name') + ' *'}
                        value={firstName}
                        onChange={setFirstName}
                        onBlur={() => markTouched('firstName')}
                        error={firstNameRequired}
                        errorText={t('account.field_required')}
                    />
                    <InputField
                        label={t('account.last_name') + ' *'}
                        value={lastName}
                        onChange={setLastName}
                        onBlur={() => markTouched('lastName')}
                        error={lastNameRequired}
                        errorText={t('account.field_required')}
                    />
                </div>
                <InputField
                    label={t('account.phone')}
                    value={phone}
                    onChange={setPhone}
                    type={'tel'}
                    placeholder={t('account.phone_placeholder')}
                />
            </div>
        </>
    );
    const orgForm = (
        <>
            <div className={'flex flex-col gap-5'}>
                <div>
                    <InputField
                        label={t('account.organization_name') + ' *'}
                        value={organizationName}
                        onChange={setOrganizationName}
                        onBlur={() => markTouched('organizationName')}
                        error={organizationNameRequired}
                        errorText={t('account.field_required')}
                    />
                    <p className={'mt-1.5 text-xs text-foreground/60'}>
                        {t('account.organization_name_hint')}
                    </p>
                </div>
                <div>
                    <SelectField
                        label={t('organization_type.label')}
                        value={organizationType}
                        onChange={(value) => setOrganizationType(value as TenantOrganizationType)}
                        // Radix ignores the view's disabled fieldset: lock explicitly.
                        disabled={!isEditing('org')}
                        options={orgTypeOptions.options}
                        optionLabels={orgTypeOptions.optionLabels}
                    />
                    <p className={'mt-1.5 text-xs text-foreground/60'}>
                        {t('organization_type.hint')}
                    </p>
                </div>
                <div>
                    <label className={'flex items-start gap-2 text-sm'}>
                        <input
                            type={'checkbox'}
                            className={'mt-0.5'}
                            checked={isKleinunternehmer}
                            onChange={(e) => setIsKleinunternehmer(e.target.checked)}
                        />
                        <span>{t('tax_category.kleinunternehmer')}</span>
                    </label>
                    <p className={'mt-1.5 text-xs text-foreground/60'}>
                        {t('tax_category.kleinunternehmer_help')}
                    </p>
                </div>
                <SelectField
                    label={t('account.country')}
                    value={country}
                    onChange={setCountry}
                    disabled={!isEditing('org')}
                    options={countryOptions}
                    optionLabels={countryLabels}
                />
            </div>
        </>
    );
    const bankForm = (
        <>
            <div className={'flex flex-col gap-5'}>
                <InputField
                    label={t('account.account_holder') + ' *'}
                    value={accountHolder}
                    onChange={setAccountHolder}
                    onBlur={() => markTouched('accountHolder')}
                    placeholder={t('account.account_holder_placeholder')}
                    error={accountHolderRequired}
                    errorText={t('account.field_required')}
                />
                <InputField
                    label={t('account.iban') + ' *'}
                    value={iban}
                    onChange={setIban}
                    onBlur={() => markTouched('iban')}
                    placeholder={'DE89 3704 0044 0532 0130 00'}
                    error={ibanFieldError}
                    errorText={ibanFieldErrorText}
                />
                <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                    <InputField
                        label={t('account.bic') + ' *'}
                        value={bic}
                        onChange={setBic}
                        onBlur={() => markTouched('bic')}
                        placeholder={'COBADEFFXXX'}
                        error={bicFieldError}
                        errorText={bicFieldErrorText}
                    />
                    <InputField
                        label={t('account.bank_name') + ' *'}
                        value={bankName}
                        onChange={setBankName}
                        onBlur={() => markTouched('bankName')}
                        placeholder={t('account.bank_name_placeholder')}
                        error={bankNameRequired}
                        errorText={t('account.field_required')}
                    />
                </div>
            </div>
        </>
    );
    const contactForm = (
        <>
            <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                <InputField
                    label={t('account.contact_email') + ' *'}
                    value={contactEmail}
                    onChange={setContactEmail}
                    onBlur={() => markTouched('contactEmail')}
                    type={'email'}
                    placeholder={'info@example.com'}
                    error={contactEmailRequired}
                    errorText={t('account.field_required')}
                />
                <InputField
                    label={t('account.contact_phone') + ' *'}
                    value={contactPhone}
                    onChange={setContactPhone}
                    onBlur={() => markTouched('contactPhone')}
                    type={'tel'}
                    placeholder={t('account.phone_placeholder')}
                    error={contactPhoneRequired}
                    errorText={t('account.field_required')}
                />
            </div>

            <div className={'mt-4'}>
                <InputField
                    label={t('account.website')}
                    value={website}
                    onChange={setWebsite}
                    type={'url'}
                    placeholder={'https://example.com'}
                />
                <p className={'mt-1.5 text-xs text-foreground/60'}>
                    {t('account.website_hint')}
                </p>
            </div>
        </>
    );
    const legalForm = (
        <>
            <div className={'flex flex-col gap-5'}>
                <div className={'text-sm text-foreground/60'}>
                    {t('account.legal_company_label')}{' '}
                    <span className={'font-medium text-foreground'}>{legalCompanyName || t('common.missing')}</span>
                </div>

                <InputField
                    label={t('account.street') + ' *'}
                    value={street}
                    onChange={setStreet}
                    onBlur={() => markTouched('street')}
                    placeholder={t('account.street_placeholder')}
                    error={streetRequired}
                    errorText={t('account.field_required')}
                />
                <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                    <InputField
                        label={t('account.postal_code') + ' *'}
                        value={postalCode}
                        onChange={setPostalCode}
                        onBlur={() => markTouched('postalCode')}
                        placeholder={'12345'}
                        error={postalCodeRequired}
                        errorText={t('account.field_required')}
                    />
                    <InputField
                        label={t('account.city') + ' *'}
                        value={city}
                        onChange={setCity}
                        onBlur={() => markTouched('city')}
                        placeholder={t('account.city_placeholder')}
                        error={cityRequired}
                        errorText={t('account.field_required')}
                    />
                </div>

                <div className={'text-sm text-foreground/60'}>
                    {t('account.legal_country_label')}{' '}
                    <span className={'font-medium text-foreground'}>
                        {legalCountryLabel}
                    </span>
                </div>

                <div className={'border-t border-foreground/5 pt-5'}>
                    <InputField
                        label={t('account.managing_director') + ' *'}
                        value={managingDirector}
                        onChange={setManagingDirector}
                        onBlur={() => markTouched('managingDirector')}
                        placeholder={t('account.managing_director_placeholder')}
                        error={managingDirectorRequired}
                        errorText={t('account.field_required')}
                    />
                    <p className={'mt-1.5 text-xs text-foreground/60'}>
                        {t('account.managing_director_hint')}
                    </p>
                </div>

                {showRegisterFields && (
                    <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                        <InputField
                            label={t('account.hrb')}
                            value={hrb}
                            onChange={setHrb}
                            placeholder={t('account.hrb_placeholder')}
                        />
                        <InputField
                            label={t('account.hrb_court')}
                            value={hrbCourt}
                            onChange={setHrbCourt}
                            placeholder={t('account.hrb_court_placeholder')}
                        />
                    </div>
                )}
                {showRegisterFields && (
                    <div>
                        <InputField
                            label={t('account.supervisory_board_chair')}
                            value={supervisoryBoardChair}
                            onChange={setSupervisoryBoardChair}
                            placeholder={t('account.supervisory_board_chair_placeholder')}
                        />
                        <p className={'mt-1.5 text-xs text-foreground/60'}>
                            {t('account.supervisory_board_chair_hint')}
                        </p>
                    </div>
                )}
                <TaxIdFields
                    vatId={vatId}
                    onVatIdChange={setVatId}
                    taxNumber={taxNumber}
                    onTaxNumberChange={setTaxNumber}
                    invoiceIssuer={accountTenant?.invoiceIssuer}
                />
            </div>
        </>
    );
    const brandingForm = (
        <>
            <div className={'flex flex-col gap-6'}>
                {/* Primary color */}
                <div>
                    <label htmlFor={'branding-primary-color'} className={'text-sm text-foreground/70 font-medium'}>
                        {t('account.primary_color')}
                    </label>
                    <div className={'mt-2 flex items-center gap-3'}>
                        <input
                            id={'branding-primary-color'}
                            type={'color'}
                            value={primaryColorHex}
                            onChange={(e) => setPrimaryColorHex(e.target.value)}
                            disabled={readOnly}
                            className={'h-10 w-14 rounded-md border border-foreground/15 bg-transparent p-1'
                            + ' disabled:cursor-not-allowed'}
                        />
                        <span
                            aria-hidden={'true'}
                            className={'h-10 w-10 rounded-md border border-foreground/15'}
                            style={{ backgroundColor: primaryColorHex }}
                        />
                        <span className={'text-sm font-medium text-foreground uppercase'}>
                            {primaryColorHex}
                        </span>
                    </div>
                    <p className={'mt-1.5 text-xs text-foreground/60'}>
                        {t('account.primary_color_hint')}
                    </p>
                </div>

                {/* Logo + favicon uploads */}
                <div className={'grid grid-cols-1 sm:grid-cols-2 gap-6'}>
                    <div>
                        <span className={'text-sm text-foreground/70 font-medium'}>
                            {t('account.logo')}
                        </span>
                        <div className={'mt-2 flex items-center gap-4'}>
                            <div className={'w-20 h-20 rounded-lg border border-foreground/15 bg-foreground/5'
                            + ' flex items-center justify-center overflow-hidden'}
                            >
                                {logoPreview || currentLogoUrl ? (
                                    <img
                                        src={logoPreview ?? currentLogoUrl ?? undefined}
                                        alt={t('account.logo')}
                                        className={'w-full h-full object-contain'}
                                    />
                                ) : (
                                    <span className={'text-xs text-foreground/40'}>
                                        {t('common.missing')}
                                    </span>
                                )}
                            </div>
                            <label
                                className={cn(
                                    'text-sm font-medium text-primary cursor-pointer',
                                    !isEditing('branding') && 'cursor-not-allowed text-primary/40',
                                )}
                            >
                                {t('account.branding_choose_file')}
                                <input
                                    type={'file'}
                                    accept={'image/png,image/jpeg,image/webp,image/svg+xml'}
                                    onChange={handleSelectLogo}
                                    disabled={readOnly}
                                    className={'hidden'}
                                />
                            </label>
                        </div>
                        <p className={'mt-1.5 text-xs text-foreground/60'}>
                            {t('account.logo_hint')}
                        </p>
                    </div>

                    <div>
                        <span className={'text-sm text-foreground/70 font-medium'}>
                            {t('account.favicon')}
                        </span>
                        <div className={'mt-2 flex items-center gap-4'}>
                            <div className={'w-20 h-20 rounded-lg border border-foreground/15 bg-foreground/5'
                            + ' flex items-center justify-center overflow-hidden'}
                            >
                                {faviconPreview || currentFaviconUrl ? (
                                    <img
                                        src={faviconPreview ?? currentFaviconUrl ?? undefined}
                                        alt={t('account.favicon')}
                                        className={'w-full h-full object-contain'}
                                    />
                                ) : (
                                    <span className={'text-xs text-foreground/40'}>
                                        {t('common.missing')}
                                    </span>
                                )}
                            </div>
                            <label
                                className={cn(
                                    'text-sm font-medium text-primary cursor-pointer',
                                    !isEditing('branding') && 'cursor-not-allowed text-primary/40',
                                )}
                            >
                                {t('account.branding_choose_file')}
                                <input
                                    type={'file'}
                                    accept={'image/png,image/x-icon,image/vnd.microsoft.icon'}
                                    onChange={handleSelectFavicon}
                                    disabled={readOnly}
                                    className={'hidden'}
                                />
                            </label>
                        </div>
                        <p className={'mt-1.5 text-xs text-foreground/60'}>
                            {t('account.favicon_hint')}
                        </p>
                    </div>
                </div>
            </div>
            {!slug && (
                <p className={'mt-4 text-sm text-foreground/60'}>
                    {t('account.branding_slug_hint')}
                </p>
            )}
        </>
    );

    return (
        <div>
            <PageHeader>
                <div className={'flex-grow'}>
                    <PageHeadline>
                        {t('account.title')}
                    </PageHeadline>
                    <p className={'text-sm text-foreground/60'}>
                        {t('account.subtitle')}
                    </p>
                </div>
                {/* Owner or master — the only callers this page admits. */}
                <CustomButton
                    title={t('onboarding_import.open')}
                    isFilled={false}
                    onClick={() => navigate(paths.onboardingImport)}
                />
            </PageHeader>

            <SegmentedTabs<AccountTab>
                className={'mb-6'}
                value={activeTab}
                onChange={setActiveTab}
                items={[
                    // Order (PO, 2026-09-16) — supersedes the 2026-08-19 staging
                    // review, which put company-level facts first and the
                    // product-specific settings last. Use cases comes second,
                    // right after who the tenant is, because it is the thing
                    // the customer actually buys and uses; everything below it
                    // is settlement and presentation of that choice.
                    //
                    // Payments stays in Account rather than following the
                    // choice into Services, for two reasons the PO weighed and
                    // kept despite the friction: it is owner-only, so a manager
                    // may rearrange Services without thereby reaching bank
                    // details (ADR-0010), and the same details are what future
                    // billing will draw on. The friction is real and accepted —
                    // a tenant who picks prepay in Services has to come back
                    // here to deposit the account those e-mails will name.
                    //
                    // Use cases stays its own tab: it is the gating axis
                    // (plan §"What Account keeps, and its tabs").
                    //
                    // Communication absorbed the former Branding tab
                    // (2026-09-16). One question — how this tenant appears to
                    // its customers — answered in one place: who to reach
                    // (contact), how they are addressed (customer e-mails),
                    // and what they see (logo, favicon, primary colour).
                    // Named for both halves rather than "Communication" alone,
                    // because a logo is not communication in everyday German
                    // and a tab has to say what is behind it.
                    //
                    // The sections keep their own save buttons: each is one
                    // editable unit with its own mutation (design-decisions ›
                    // Bearbeiten und Speichern). Merging tabs did not merge
                    // saves — this page has never had a page-level save bar.
                    { key: 'organisation', label: t('account.tab_organisation') },
                    { key: 'usecases', label: t('account.tab_use_cases') },
                    { key: 'communication', label: t('account.tab_communication') },
                    { key: 'payments', label: t('account.tab_payments') },
                ]}
            />

            <fieldset disabled={readOnly} className={'flex flex-col gap-6 max-w-2xl border-0 p-0 m-0 min-w-0'}>
                {readOnly && (
                    <div className={'rounded-[5px] border border-foreground/15 bg-foreground/5 px-4 py-3 text-sm text-foreground/70'}>
                        {t('account.read_only_notice')}
                    </div>
                )}

                {activeTab === 'organisation' && (<>
                    {/* Personal Information */}
                    {!readOnly && (
                        <EditableSection
                            title={t('account.personal_section')}
                            description={t('account.personal_section_desc')}
                            readOnly={readOnly}
                            editing={isEditing('personal')}
                            isDirty={personalDirty}
                            saving={personalSaving}
                            canSave={!!firstName.trim() && !!lastName.trim()}
                            error={personalError}
                            onEdit={() => startEdit('personal')}
                            onCancel={() => cancelUnit('personal')}
                            onSave={() => { void handleSavePersonal(); }}
                            view={<fieldset disabled className={'border-0 p-0 m-0 min-w-0'}>{personalForm}</fieldset>}
                        >
                            {personalForm}
                        </EditableSection>
                    )}

                </>)}
                {activeTab === 'organisation' && (<>
                    {/* Users — the tenant's team roster. Owner can add colleagues +
                        pick their role; read-only for others. Rendered for the
                        caller's own tenant only (a master viewing another tenant by
                        slug wouldn't get that tenant's roster from this endpoint). */}
                    {ownAccountSections && <UsersSection readOnly={readOnly} />}

                </>)}
                {activeTab === 'organisation' && (<>
                    {/* Organization */}
                    <EditableSection
                        title={t('account.organization_section')}
                        description={t('account.organization_section_desc')}
                        readOnly={readOnly}
                        editing={isEditing('org')}
                        isDirty={orgDirty}
                        saving={orgSaving}
                        canSave={!!organizationName.trim() && !!country}
                        error={orgError}
                        onEdit={() => startEdit('org')}
                        onCancel={() => cancelUnit('org')}
                        onSave={() => { void handleSaveOrganization(); }}
                        view={<fieldset disabled className={'border-0 p-0 m-0 min-w-0'}>{orgForm}</fieldset>}
                    >
                        {orgForm}
                    </EditableSection>

                </>)}
                {activeTab === 'payments' && (<>
                    {/* The two credentials, kept together. The provider block
                        records which provider the tenant WANTS — connecting
                        stays a manual step kolula performs — and it is shown
                        first because the Services area links here when a
                        channel it offers needs a connection this tenant does
                        not have. Owner path; the master by-slug variant
                        renders below, outside this disabled fieldset. */}
                    {ownAccountSections && (
                        <PaymentProviderSection
                            key={`payment-provider-${accountTenant?.slug ?? ''}`}
                            readOnly={readOnly}
                            integration={paymentIntegration}
                            desiredPaymentProvider={accountTenant?.desiredPaymentProvider ?? null}
                        />
                    )}

                    {/* Invoice issuance (ADR-0012 WP0) — the third
                        billing-gating fact on this tab. Owner path; a master
                        on a foreign tenant gets the editable by-slug variant
                        below, outside this disabled fieldset. */}
                    {ownAccountSections && (
                        <InvoiceIssuerSection
                            key={`invoice-issuer-${accountTenant?.slug ?? ''}`}
                            readOnly={readOnly}
                            invoiceIssuer={accountTenant?.invoiceIssuer ?? null}
                        />
                    )}

                    {/* The Zahlenreihe kolula allocates from (#1637 WP1).
                        Renders nothing unless the tenant is in issuing mode —
                        a number circle is meaningless to a Mode C tenant. */}

                    {ownAccountSections && (
                        <InvoiceCircleSection
                            key={`invoice-circle-${accountTenant?.slug ?? ''}`}
                            readOnly={readOnly}
                            invoiceIssuer={accountTenant?.invoiceIssuer ?? null}
                        />
                    )}

                    {/* Where issued invoices are mailed (invoice dispatch,
                        precedence level 2). Beside the circle because both are
                        "how kolula invoices for me", and both are Mode A only. */}
                    {ownAccountSections && (
                        <InvoiceDispatchEmailSection
                            key={`invoice-dispatch-${accountTenant?.slug ?? ''}`}
                            readOnly={readOnly}
                            invoiceIssuer={accountTenant?.invoiceIssuer ?? null}
                            invoiceDispatchEmail={accountTenant?.invoiceDispatchEmail ?? null}
                        />
                    )}

                    {/* How extras read on the invoice (#3427 item 7). Mode A only. */}
                    {ownAccountSections && (
                        <InvoiceBundleExtrasSection
                            key={`invoice-bundle-${accountTenant?.slug ?? ''}`}
                            readOnly={readOnly}
                            invoiceIssuer={accountTenant?.invoiceIssuer ?? null}
                            invoiceBundleExtrasIntoStay={accountTenant?.invoiceBundleExtrasIntoStay === true}
                        />
                    )}

                    {/* GoBD audit export (#1644). Owner or master — the
                        server enforces it; the section only hides for Mode C. */}
                    {ownAccountSections && (
                        <AuditExportSection invoiceIssuer={accountTenant?.invoiceIssuer ?? null} />
                    )}

                    {/* Bank Details */}
                    <EditableSection
                        id={'bank-details'}
                        title={t('account.bank_section')}
                        description={t('account.bank_section_desc')}
                        readOnly={readOnly}
                        editing={isEditing('bank')}
                        isDirty={bankDirty}
                        saving={bankSaving}
                        canSave={bankFieldsComplete && bankFormatValid}
                        error={bankError}
                        onEdit={() => startEdit('bank')}
                        onCancel={() => cancelUnit('bank')}
                        onSave={() => { void handleSaveBankDetails(); }}
                        view={<fieldset disabled className={'border-0 p-0 m-0 min-w-0'}>{bankForm}</fieldset>}
                    >
                        {bankForm}
                    </EditableSection>
                </>)}
                {activeTab === 'usecases' && (
                    // Which use cases the tenant offers — the enable switch
                    // (plan, phase 2). Enabling one makes its configuration
                    // section appear in Services.
                    <UseCasesSection
                        key={`use-cases-${accountTenant?.slug ?? ''}`}
                        readOnly={readOnly}
                        services={accountTenant?.services ?? null}
                        servicesConfigured={accountTenant?.servicesConfigured ?? false}
                        isTrial={accountTenant?.plan === 'trial'}
                        tenantKey={accountTenant?.slug ?? undefined}
                        onDirtyChange={setUseCasesDirty}
                    />
                )}
                {activeTab === 'communication' && (<>
                    {/* Public contact */}
                    <EditableSection
                        id={'contact'}
                        title={t('account.contact_section')}
                        description={t('account.contact_section_desc')}
                        readOnly={readOnly}
                        editing={isEditing('contact')}
                        isDirty={contactDirty}
                        saving={contactSaving}
                        canSave={contactComplete}
                        error={contactError}
                        onEdit={() => startEdit('contact')}
                        onCancel={() => cancelUnit('contact')}
                        onSave={() => { void handleSaveContact(); }}
                        view={<fieldset disabled className={'border-0 p-0 m-0 min-w-0'}>{contactForm}</fieldset>}
                    >
                        {contactForm}
                    </EditableSection>

                </>)}
                {activeTab === 'communication' && (<>
                    {/* Customer emails — German salutation (du / Sie) for the mails
                        we send to this tenant's customers. English is unaffected (no
                        T–V distinction). One editable unit like the others. #1642. */}
                    <EditableSection
                        id={'customer-emails'}
                        title={t('account.emails_section')}
                        description={t('account.emails_section_desc')}
                        readOnly={readOnly}
                        editing={anredeEdit.editing}
                        isDirty={anredeEdit.isDirty}
                        saving={anredeSaving}
                        onEdit={anredeEdit.startEdit}
                        onCancel={anredeEdit.cancel}
                        onSave={() => { void handleSaveAnrede(); }}
                        view={(
                            <div className={'flex flex-col gap-1 text-sm'}>
                                <span className={'text-foreground/70 font-medium'}>{t('account.anrede_label')}</span>
                                <span className={'text-foreground'}>
                                    {t(`account.anrede_${anredeEdit.value}`)}
                                </span>
                                <p className={'mt-1 text-xs text-foreground/60'}>{t('account.anrede_hint')}</p>
                            </div>
                        )}
                    >
                        <div className={'flex flex-col gap-2'}>
                            <span className={'text-sm text-foreground/70 font-medium'}>
                                {t('account.anrede_label')}
                            </span>
                            <SegmentedTabs<TenantAnrede>
                                items={[
                                    { key: 'sie', label: t('account.anrede_sie') },
                                    { key: 'du', label: t('account.anrede_du') },
                                ]}
                                value={anredeEdit.value}
                                onChange={anredeEdit.setDraft}
                            />
                            <p className={'mt-1 text-xs text-foreground/60'}>
                                {t('account.anrede_hint')}
                            </p>
                        </div>
                    </EditableSection>

                    {/* Owner-only on the server; a master viewing another
                        tenant, or a non-owner, does not get the section — and
                        neither does a legacy tenant, which has no operational
                        tenant row to store the sender on. */}
                    {ownAccountSections && !readOnly && isSelfServiceTenant(selectedTenant)
                        && <SenderAddressSection />}

                </>)}
                {activeTab === 'organisation' && (<>
                    {/* Legal Notice / Impressum */}
                    <EditableSection
                        id={'legal-notice'}
                        title={t('account.legal_section')}
                        description={t('account.legal_section_desc')}
                        readOnly={readOnly}
                        editing={isEditing('legal')}
                        isDirty={legalDirty}
                        saving={legalSaving}
                        canSave={legalNoticeComplete}
                        error={legalError}
                        onEdit={() => startEdit('legal')}
                        onCancel={() => cancelUnit('legal')}
                        onSave={() => { void handleSaveLegalNotice(); }}
                        view={<fieldset disabled className={'border-0 p-0 m-0 min-w-0'}>{legalForm}</fieldset>}
                    >
                        {legalForm}
                    </EditableSection>

                    {/* Postpay legal texts, beside the legal notice (ADR-0005:
                        the tenant's legal identity lives in Account). The owner
                        edits them; a master viewing the tenant reads them —
                        see `AccountLegalTexts`. */}
                    <AccountLegalTexts
                        // Drafts belong to one tenant: remount on a switch (#909/#929).
                        key={`legal-texts-${accountTenant?.slug ?? ''}`}
                        ownsTenant={ownAccountSections}
                        isSelfService={isSelfService}
                        readOnly={readOnly}
                        legalTexts={accountTenant?.legalTexts ?? null}
                    />

                    {/* The data protection officer on the booking page's privacy
                        policy — same audience as the legal texts above. */}
                    <AccountPrivacyOfficer
                        // Drafts belong to one tenant: remount on a switch (#909/#929).
                        key={`privacy-officer-${accountTenant?.slug ?? ''}`}
                        ownsTenant={ownAccountSections}
                        isSelfService={isSelfService}
                        readOnly={readOnly}
                        privacyOfficer={accountTenant?.privacyOfficer ?? null}
                        organizationName={accountTenant?.organizationName ?? ''}
                    />

                </>)}
                {/* Renders last inside the Communication tab: the organisation
                    sections between it and the two above are not mounted while
                    this tab is active, so DOM order is contact → customer
                    e-mails → branding without moving the block. */}
                {activeTab === 'communication' && (<>
                    {/* Branding — per-tenant primary color + logo / favicon for the
                        shared end-user deployment (app.kolula.com/{slug}). Each field
                        overwrites just its own branding key server-side; unset fields
                        fall back to the kolula defaults in the end-user app. Needs a
                        slug (the S3 path is derived from it), mirroring booking-page. */}
                    <EditableSection
                        id={'branding'}
                        title={t('account.branding_section')}
                        description={t('account.branding_section_desc')}
                        readOnly={readOnly}
                        editing={isEditing('branding')}
                        isDirty={brandingDirty}
                        saving={brandingSaving}
                        canSave={!!slug}
                        error={brandingError}
                        onEdit={() => startEdit('branding')}
                        onCancel={() => cancelUnit('branding')}
                        onSave={() => { void handleSaveBranding(); }}
                        view={<fieldset disabled className={'border-0 p-0 m-0 min-w-0'}>{brandingForm}</fieldset>}
                    >
                        {brandingForm}
                    </EditableSection>

                    {/* Browser-tab title and description of the booking page —
                        beside branding, which shapes the same page. Only for
                        tenants whose booking page reads its config at runtime. */}
                    {isSelfService && (
                        <SeoSection
                            key={`seo-${accountTenant?.slug ?? ''}`}
                            readOnly={readOnly}
                            seo={accountTenant?.seo ?? null}
                            organizationName={accountTenant?.organizationName ?? ''}
                        />
                    )}

                </>)}
                {activeTab === 'organisation' && (<>
                    {/* "Cancel account" (#1379): archive for operational-DB
                        tenants, deletion request for legacy ones. */}
                    {!readOnly && (
                        <CancelAccountSection archive={isSelfService} organizationName={organizationName} />
                    )}
                </>)}
            </fieldset>

            {/* Master path: the viewed tenant's check-in/checkout config is
                editable (unlike the rest of the account, which is read-only for a
                master), so it lives OUTSIDE the disabled fieldset above. Writes go
                to the master by-slug endpoint. Only for self-service tenants with
                a resolved slug (master account is fetched by slug). Skipped when
                the master owns this tenant — that renders the editable owner
                path above instead, avoiding a duplicate section. */}
            {!ownAccountSections && isSelfService && selectedTenant && (
                <div className={'max-w-2xl mt-6 flex flex-col gap-6'}>
                    {/* #1379: a closed account, seen by kolula support. The
                        only action on it is reactivating within the window. */}
                    {activeTab === 'organisation' && accountTenant?.archivedAt && (
                        <ReactivateAccountSection
                            key={`ReactivateAccountSection-${selectedTenant}`}
                            slug={selectedTenant}
                            archivedAt={accountTenant.archivedAt}
                            deletionDueAt={accountTenant.deletionDueAt}
                        />
                    )}
                    {/* Invoice issuance is master-editable by slug (recording
                        an existing customer's arrangement — InnoDom: external),
                        so it joins the check-in/checkout section outside the
                        read-only fieldset. Shown with its tab, like the owner
                        variant above. */}
                    {activeTab === 'payments' && (<>
                        <PaymentProviderSection
                            // An open draft belongs to one tenant (#909/#929).
                            key={`PaymentProviderSection-${selectedTenant}`}
                            readOnly={false}
                            integration={paymentIntegration}
                            desiredPaymentProvider={accountTenant?.desiredPaymentProvider ?? null}
                            masterSlug={selectedTenant}
                        />
                        <InvoiceIssuerSection
                            // An open draft belongs to one tenant (#909/#929).
                            key={`InvoiceIssuerSection-${selectedTenant}`}
                            readOnly={false}
                            invoiceIssuer={accountTenant?.invoiceIssuer ?? null}
                            masterSlug={selectedTenant}
                        />
                        {/* The number circle and the audit export, as the
                            owner sees them. Both already address the displayed
                            tenant for a master (the circle by slug, the export
                            by the tenant header) and the server allows a master
                            on both; they were simply never rendered here, so
                            kolula staff could not see a tenant's numbering. */}
                        <InvoiceCircleSection
                            // An open draft belongs to one tenant (#909/#929).
                            key={`InvoiceCircleSection-${selectedTenant}`}
                            invoiceIssuer={accountTenant?.invoiceIssuer ?? null}
                        />
                        <InvoiceDispatchEmailSection
                            // An open draft belongs to one tenant (#909/#929).
                            key={`InvoiceDispatchEmailSection-${selectedTenant}`}
                            invoiceIssuer={accountTenant?.invoiceIssuer ?? null}
                            invoiceDispatchEmail={accountTenant?.invoiceDispatchEmail ?? null}
                        />
                        <InvoiceBundleExtrasSection
                            // An open draft belongs to one tenant (#909/#929).
                            key={`InvoiceBundleExtrasSection-${selectedTenant}`}
                            invoiceIssuer={accountTenant?.invoiceIssuer ?? null}
                            invoiceBundleExtrasIntoStay={accountTenant?.invoiceBundleExtrasIntoStay === true}
                        />
                        <AuditExportSection invoiceIssuer={accountTenant?.invoiceIssuer ?? null} />
                    </>)}
                </div>
            )}

        </div>
    );
};

export default AccountPage;
